import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { Branch } from 'entities/branch';
import { Customer } from 'entities/customer';
import { PartSearch, Refinement } from 'entities/part-search';
import { PartSearchResponse } from 'entities/parts/part-search-response';
import { ToastType } from 'entities/toast-type';
import { BehaviorSubject, Observable, OperatorFunction, combineLatest, of } from 'rxjs';
import { filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { SourceLocationType } from 'services/logger.service';
import { LoyaltyService } from 'services/loyalty.service';
import { PartService } from 'services/part.service';
import { ToastService } from 'services/toast.service';
import { TrackerService } from 'services/tracker.service';
import { AppState } from 'store/app-state';
import * as BranchSelectors from 'store/branch/branch.selectors';
import * as CustomerSelectors from 'store/customer/customer.selectors';
import * as LoyaltySelectors from 'store/loyalty/loyalty.selectors';

interface QueryState {
  term: string;
  partId: string;
  isExpandedSearch: boolean;
  facets: Refinement[];
  pageNumber: number;
}

interface PartBasePortableComponentState {
  query: QueryState;
  result: PartSearchResponse;
  loading: boolean;
  dismissedCreatePart: boolean;
  overrideBranchCode?: string;
  overrideRewardNumber?: string;
}

@Injectable()
export class PartBasePortableComponentStore extends ComponentStore<PartBasePortableComponentState> {
  public readonly pageSize = 20;

  private readonly querySubject = new BehaviorSubject<QueryState>({
    term: '',
    partId: '',
    isExpandedSearch: false,
    facets: [],
    pageNumber: 1
  });

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private partService: PartService,
    private toastService: ToastService,
    private trackerService: TrackerService,
    private loyaltyService: LoyaltyService
  ) {
    super({
      query: {
        term: '',
        partId: '',
        isExpandedSearch: false,
        facets: [],
        pageNumber: 1
      },
      result: null,
      loading: false,
      dismissedCreatePart: false,
      overrideBranchCode: undefined,
      overrideRewardNumber: undefined
    });
  }

  public init(isInModal: boolean, initialSearchTerm?: string, initialPartId?: string, activatedRoute?: ActivatedRoute, initialPageNumber?: number, branchCode?: string, rewardNumber?: string): void {
    // Store override values in state
    if (branchCode || rewardNumber) {
      this.patchState({
        overrideBranchCode: branchCode,
        overrideRewardNumber: rewardNumber
      });
    }

    if (isInModal) {
      // Modal mode: use initial inputs directly
      const initialQuery: QueryState = {
        term: initialSearchTerm || '',
        partId: initialPartId || '',
        isExpandedSearch: false,
        facets: [],
        pageNumber: initialPageNumber || 1
      };
      
      this.querySubject.next(initialQuery);
      this.searchPartsEffect(of(initialQuery));
    } else {
      // Route mode: watch query params from activated route
      this.initFromRoute(activatedRoute);
    }
  }

  private initFromRoute(activatedRoute: ActivatedRoute): void {
    activatedRoute.queryParams
      .pipe(
        map((queryParams) => ({
          term: queryParams['searchTerm'] || '',
          partId: queryParams['partId'] || '',
          isExpandedSearch: queryParams['isExpandedSearch'] === 'true',
          pageNumber: Number(queryParams['page']) || 1
        })),
        filter((queryParams) => {
          const queryState = this.get((state) => state.query);
          return queryState.term !== queryParams.term
            || queryState.partId !== queryParams.partId
            || queryState.isExpandedSearch !== queryParams.isExpandedSearch
            || queryState.pageNumber !== queryParams.pageNumber;
        }),
        map((queryParams): QueryState => ({
          ...queryParams,
          facets: []
        }))
      )
      .subscribe(query => {
        this.querySubject.next(query);
        this.searchPartsEffect(of(query));
      });
  }

  public readonly updatePage = this.updater((state, page: number) => {
    const newQuery = {
      ...state.query,
      pageNumber: page
    };
    this.querySubject.next(newQuery);
    this.searchPartsEffect(of(newQuery));
    return {
      ...state,
      query: newQuery
    };
  });

  public readonly updateFacets = this.effect((facets$: Observable<Refinement[]>) =>
    facets$
      .pipe(
        tap((newFacets) => {
          const newQuery = {
            ...this.get((state) => state.query),
            facets: newFacets,
            pageNumber: 1
          };
          this.patchState({
            query: newQuery
          });
          this.querySubject.next(newQuery);
        }),
        switchMap(() => this.querySubject.asObservable()),
        this.searchParts()
      )
  );

  public readonly navigateToPartDetails = this.updater((state, partId: string) => {
    const newQuery = {
      ...state.query,
      partId: partId,
      pageNumber: 1
    };
    this.querySubject.next(newQuery);
    this.searchPartsEffect(of(newQuery));
    return {
      ...state,
      query: newQuery
    };
  });

  public readonly searchForPart = this.updater((state, params: { term: string, partId: string }) => {
    const newQuery = {
      ...state.query,
      term: params.term,
      partId: params.partId,
      isExpandedSearch: false,
      facets: [],
      pageNumber: 1
    };
    this.querySubject.next(newQuery);
    this.searchPartsEffect(of(newQuery));
    return {
      ...state,
      query: newQuery
    };
  });

  public readonly clearFacets = this.updater((state) => ({
    ...state,
    query: {
      ...state.query,
      facets: []
    },
  }));

  public readonly dismissCreatePart = this.updater((state) => ({
    ...state,
    dismissedCreatePart: true
  }));

  private readonly query$ = this.select((state) => state.query);
  public readonly queryTerm$ = this.select(this.query$, (query) => query.term);
  public readonly pageNumber$ = this.select(this.query$, (query) => query.pageNumber);
  public readonly loading$ = this.select((state) => state.loading);
  public readonly result$ = this.select((state) => state.result);
  public readonly count$ = this.select(this.result$, (result) => result?.totalParts ?? 0);
  public readonly showCreatePart$ = this.select((state) => !state.loading && !state.result?.totalParts && !state.dismissedCreatePart);
  public readonly facets$ = this.select(this.query$, (query) => query.facets);

  private searchPartsEffect(query$: Observable<QueryState>): void {
    query$.pipe(this.searchParts()).subscribe();
  }

  private searchParts(): OperatorFunction<QueryState, void> {
    return (query$: Observable<QueryState>) =>
      combineLatest([this.store.select(LoyaltySelectors.loyaltyAccount), query$])
        .pipe(
          map(([_, newQuery]) => newQuery),
          tap((newQuery) => {
            this.patchState({
              query: {
                ...this.get((state) => state.query),
                term: newQuery.term,
                partId: newQuery.partId,
                isExpandedSearch: newQuery.isExpandedSearch,
                pageNumber: newQuery.pageNumber
              }
            });
          }),
          switchMap((query) => {
            const state = this.get();
            console.log('[Store] searchParts - state overrides:', state.overrideBranchCode, state.overrideRewardNumber);
            
            // If we have override values, use them directly
            if (state.overrideBranchCode && state.overrideRewardNumber) {
              console.log('[Store] Using override values - branchCode:', state.overrideBranchCode, 'rewardNumber:', state.overrideRewardNumber);
              return of<[QueryState, Branch, Customer]>([query, { code: state.overrideBranchCode } as Branch, { rewardNumber: state.overrideRewardNumber } as Customer]);
            }
            console.log('[Store] No overrides, using store selectors');
            
            // Otherwise, get from store
            return combineLatest([
              this.store.select(BranchSelectors.selectedBranch),
              this.store.select(CustomerSelectors.selectedCustomer)
            ])
              .pipe(
                filter(([branch, customer]) => Boolean(branch) && Boolean(customer)),
                map(([branch, customer]): [QueryState, Branch, Customer] => [query, branch, customer])
              );
          }),
          filter(([query, branch, customer]) => branch.code !== null && customer.rewardNumber !== null),
          tap(() => {
            this.patchState({
              loading: true,
              dismissedCreatePart: false
            });
          }),
          map(([query, branch, customer]): [PartSearch, Branch] => {
            const partSearch = {
              branchCode: branch.code,
              rewardNumber: customer.rewardNumber,
              partSearchTerm: query.term,
              partId: query.partId,
              isExpandedSearch: query.isExpandedSearch,
              isCountCheck: false,
              refinements: this.get((state) => state.query).facets,
              pageNumber: query.pageNumber,
              pageSize: this.pageSize
            };
            console.log('[Store] Calling searchParts API with:', partSearch);
            return [partSearch, branch];
          }),
          switchMap(([partSearch, branch]) => this.partService.searchParts(partSearch)
            .pipe(
              tap((response) => {
                if (response.ErrorType && response.ErrorType !== 200) {
                  throw new Error(response);
                }
              }),
              map(result => [result, branch] as [PartSearchResponse, Branch]),
              withLatestFrom(this.store.select(LoyaltySelectors.loyaltyAccount)),
              switchMap(([[result, branch], loyaltyAccount]) => {
                console.log('[Store] Loyalty discount using branch:', branch?.code);
                if (loyaltyAccount) {
                  const partSearchResponse: PartSearchResponse = result;
                  return this.loyaltyService.getDiscounts({
                    memberId: loyaltyAccount.loyaltyAccountIdentifier,
                    partNumbers: result?.parts?.map(x => x.rushPartNumber),
                    branchCode: branch.code,
                    loyaltyProgram: loyaltyAccount.loyaltyProgram
                  }).pipe(
                    map((discounts) => {
                      partSearchResponse.parts = partSearchResponse.parts.map(part => {
                        const discount: any = discounts[part.rushPartNumber.split(":")[0]];
                        if (discount) {
                          const loyaltyProgram = loyaltyAccount.loyaltyProgram;
                          part.sapCoupons = [
                            ...part.sapCoupons,
                            {
                              amount: discount.amount,
                              description: discount.code + " " + loyaltyProgram.charAt(0).toUpperCase() + loyaltyProgram.slice(1),
                              type: 'part'
                            }
                          ];
                        }
                        return part;
                      });
                      return partSearchResponse;
                    })
                  );
                }
                return of(result);
              }),
              tapResponse(
                (result: PartSearchResponse) => {
                  const source = SourceLocationType[SourceLocationType.PartListResult];
                  this.trackerService.trackPartSearch(result.parts, partSearch.partSearchTerm, source);

                  if (!result || result.totalParts <= 0) {
                    this.toastService.showToast('Part not found.', ToastType.Error);
                  }
                  if (result && result.totalParts > 1 && result.isExpandedSearch) {
                    this.toastService.showToast('Your results contain parts from an expanded industry search', ToastType.Success);
                  }

                  this.setState((state) => ({
                    ...state,
                    query: {
                      ...state.query,
                      isExpandedSearch: result.isExpandedSearch,
                      pageNumber: partSearch.pageNumber
                    },
                    result,
                    loading: false,
                    dismissedCreatePart: false
                  }));

                  // Only update route if not in modal
                  if (this.router.url.includes('/parts')) {
                    this.router.navigate(['/parts'], {
                      queryParams: {
                        isExpandedSearch: result.isExpandedSearch,
                        page: partSearch.pageNumber
                      },
                      queryParamsHandling: "merge",
                      onSameUrlNavigation: "ignore"
                    });
                  }
                },
                (error) => {
                  this.toastService.errorMessage('PartBaseComponent', 'getParts', 'getParts', null);
                  this.patchState({ loading: false, dismissedCreatePart: false });
                }
              )
            )
          ),
          map(() => void 0)
        );
  }
}
